import { Icon } from '@iconify-icon/react';
import Link from 'next/link';
import { NextSeo } from 'next-seo';

import { DocsArticleLayout } from '@/components/modules/Docs';
import PageLink from '@/utils/PageLink';

const tableOfContents = [
  { id: 'introduction', title: '1. Introduction' },
  { id: 'myth-coding', title: '2. Myth: You Need to Code' },
  { id: 'myth-expensive', title: '3. Myth: AI Is Too Expensive' },
  { id: 'myth-time', title: '4. Myth: Training Takes Weeks' },
  { id: 'myth-data', title: '5. Myth: You Need Massive Data' },
  { id: 'myth-enterprise', title: '6. Myth: AI Is Only for Enterprises' },
  { id: 'myth-replace', title: '7. Myth: AI Replaces Humans' },
  { id: 'myth-accuracy', title: "8. Myth: AI Can't Be Accurate" },
  { id: 'get-started', title: '9. Start Building Today' },
];

const AiMythsPage = () => {
  return (
    <>
      <NextSeo
        title="7 AI Chatbot Myths Debunked"
        description="Discover the truth behind common AI chatbot myths. Learn how anyone can build sophisticated AI assistants without coding, massive budgets, or technical expertise."
        canonical="https://chat.co/docs/ai-myths"
        openGraph={{
          url: 'https://chat.co/docs/ai-myths',
          title: '7 AI Chatbot Myths Debunked | Chat.co',
          description:
            'Discover the truth behind common AI chatbot myths. Learn how anyone can build sophisticated AI assistants without coding, massive budgets, or technical expertise.',
          type: 'article',
          article: {
            publishedTime: '2026-01-01T00:00:00Z',
            modifiedTime: '2026-01-26T00:00:00Z',
            section: 'Getting Started',
            tags: ['AI myths', 'chatbot misconceptions', 'AI for beginners', 'no-code AI'],
          },
          images: [
            {
              url: 'https://chat.co/images/og-docs.png',
              width: 1200,
              height: 630,
              alt: 'Chat.co - AI Chatbot Myths Debunked',
            },
          ],
        }}
        twitter={{
          cardType: 'summary_large_image',
        }}
      />

      <DocsArticleLayout
        category="Getting Started"
        categoryColor="text-green-700"
        title="7 AI Chatbot Myths Debunked"
        description="The truth about building AI assistants—it's easier than you think."
        readingTime="8 min read"
        lastUpdated="January 2026"
        tableOfContents={tableOfContents}
        prevArticle={{
          title: 'Knowledge Upload Playbook',
          href: PageLink.docsKnowledgeUpload,
        }}
        nextArticle={{
          title: 'Build a RAG System',
          href: PageLink.docsBuildRagSystem,
        }}
        keyTakeaways={[
          "No coding required—Chat.co's visual interface handles all technical complexity",
          'Launch in 10 minutes, not months, with affordable pricing starting at $49/month',
          'One document is enough to start—quality matters more than quantity',
          'AI augments your team rather than replacing humans',
          'RAG technology with citations ensures accurate, trustworthy responses',
        ]}>
        {/* Introduction */}
        <section id="introduction" className="scroll-mt-24">
          <h2>1. Introduction</h2>
          <p className="lead text-lg text-gray-700">
            When it comes to AI chatbots, misconceptions abound. Many businesses believe that
            creating an intelligent virtual assistant requires a team of developers, months of work,
            or an enterprise-level budget. The reality? None of that is true anymore.
          </p>

          {/* Myth Scorecard Infographic */}
          <div className="my-8 rounded-xl border border-gray-200 bg-gradient-to-br from-gray-50 to-white p-6">
            <div className="mb-4 flex items-center justify-between">
              <h4 className="font-semibold text-gray-900">The 7 Myths We&apos;ll Bust</h4>
              <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
                All Debunked
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {[
                { myth: 'Need to code', icon: 'tabler:code-off', truth: 'Visual builder' },
                { myth: 'Too expensive', icon: 'tabler:currency-dollar-off', truth: 'From $49/mo' },
                { myth: 'Takes weeks', icon: 'tabler:clock-off', truth: '10 minutes' },
                { myth: 'Need big data', icon: 'tabler:database-off', truth: '1 doc works' },
                { myth: 'Enterprise only', icon: 'tabler:building-off', truth: 'Any size' },
                { myth: 'Replaces humans', icon: 'tabler:robot-off', truth: 'Augments teams' },
                { myth: "Can't trust AI", icon: 'tabler:shield-off', truth: 'RAG + citations' },
              ].map((item, index) => (
                <div
                  key={index}
                  className="group relative overflow-hidden rounded-lg border border-gray-100 bg-white p-3 transition-all hover:border-green-200 hover:shadow-sm">
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-red-50 transition-colors group-hover:bg-green-50">
                      <Icon
                        icon="tabler:x"
                        className="text-sm text-red-400 transition-all group-hover:hidden"
                      />
                      <Icon
                        icon="tabler:check"
                        className="hidden text-sm text-green-500 transition-all group-hover:block"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-medium text-gray-500 line-through decoration-red-300 group-hover:text-gray-400">
                        {item.myth}
                      </p>
                      <p className="truncate text-xs font-semibold text-green-600 opacity-0 transition-opacity group-hover:opacity-100">
                        {item.truth}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-center text-xs text-gray-500">
              Hover over each myth to see the reality
            </p>
          </div>

          <p>
            Let&apos;s separate fact from fiction and discover how easy it really is to bring
            AI-powered customer support to your business.
          </p>
        </section>

        {/* Myth 1: You Need to Know Programming */}
        <section id="myth-coding" className="scroll-mt-24">
          <h2>2. Myth: You Need to Know Programming</h2>

          <div className="my-6 grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-red-200 bg-red-50 p-5">
              <div className="flex items-center gap-2 text-red-700">
                <Icon icon="tabler:x" className="text-xl" />
                <span className="font-semibold">The Myth</span>
              </div>
              <p className="mt-2 text-red-800">
                &quot;Building an AI chatbot requires programming skills, machine learning
                expertise, and a deep understanding of natural language processing.&quot;
              </p>
            </div>
            <div className="rounded-lg border border-green-200 bg-green-50 p-5">
              <div className="flex items-center gap-2 text-green-700">
                <Icon icon="tabler:check" className="text-xl" />
                <span className="font-semibold">The Reality</span>
              </div>
              <p className="mt-2 text-green-800">
                Anyone can build a sophisticated AI chatbot with zero coding knowledge.
                Chat.co&apos;s visual interface handles all the technical complexity for you.
              </p>
            </div>
          </div>

          <h3>How Chat.co Makes It Easy</h3>
          <ul>
            <li>
              <strong>Visual bot builder</strong> — Create and configure your chatbot through an
              intuitive dashboard
            </li>
            <li>
              <strong>Drag-and-drop uploads</strong> — Simply upload your documents, and our AI does
              the rest
            </li>
            <li>
              <strong>One-click deployment</strong> — Copy a single line of code to add the chatbot
              to any website
            </li>
            <li>
              <strong>No ML knowledge needed</strong> — We handle embeddings, vector search, and
              retrieval automatically
            </li>
          </ul>

          <div className="my-6 rounded-lg border-l-4 border-blue-500 bg-blue-50 p-4">
            <p className="text-sm text-blue-900">
              <strong>Real example:</strong> Marketing teams, HR departments, and small business
              owners use Chat.co daily—most have never written a line of code. If you can use Google
              Docs, you can build an AI chatbot.
            </p>
          </div>
        </section>

        {/* Myth 2: AI Chatbots Are Expensive */}
        <section id="myth-expensive" className="scroll-mt-24">
          <h2>3. Myth: AI Chatbots Are Too Expensive</h2>

          <div className="my-6 grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-red-200 bg-red-50 p-5">
              <div className="flex items-center gap-2 text-red-700">
                <Icon icon="tabler:x" className="text-xl" />
                <span className="font-semibold">The Myth</span>
              </div>
              <p className="mt-2 text-red-800">
                &quot;Custom AI solutions cost tens of thousands of dollars and require ongoing
                infrastructure investments.&quot;
              </p>
            </div>
            <div className="rounded-lg border border-green-200 bg-green-50 p-5">
              <div className="flex items-center gap-2 text-green-700">
                <Icon icon="tabler:check" className="text-xl" />
                <span className="font-semibold">The Reality</span>
              </div>
              <p className="mt-2 text-green-800">
                Chat.co offers plans starting at affordable monthly rates, with no hidden costs for
                infrastructure, maintenance, or AI model access.
              </p>
            </div>
          </div>

          <h3>Compare the True Costs</h3>
          <div className="my-6 overflow-hidden rounded-lg border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-900">Approach</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-900">Cost Range</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-900">
                    Time to Launch
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr>
                  <td className="px-4 py-3 text-gray-700">Custom development</td>
                  <td className="px-4 py-3 text-gray-700">$50,000 - $500,000+</td>
                  <td className="px-4 py-3 text-gray-700">3-12 months</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-gray-700">Hire AI consultants</td>
                  <td className="px-4 py-3 text-gray-700">$10,000 - $100,000+</td>
                  <td className="px-4 py-3 text-gray-700">1-6 months</td>
                </tr>
                <tr className="bg-green-50">
                  <td className="px-4 py-3 font-medium text-green-800">Chat.co</td>
                  <td className="px-4 py-3 font-medium text-green-800">Starting at $49/month</td>
                  <td className="px-4 py-3 font-medium text-green-800">10 minutes</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p>
            With Chat.co, you get enterprise-grade AI capabilities without the enterprise price tag.
            Pay for what you use, scale as you grow.
          </p>
        </section>

        {/* Myth 3: Training Takes Weeks */}
        <section id="myth-time" className="scroll-mt-24">
          <h2>4. Myth: Training an AI Takes Weeks</h2>

          <div className="my-6 grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-red-200 bg-red-50 p-5">
              <div className="flex items-center gap-2 text-red-700">
                <Icon icon="tabler:x" className="text-xl" />
                <span className="font-semibold">The Myth</span>
              </div>
              <p className="mt-2 text-red-800">
                &quot;Training an AI model requires weeks of data preparation, model fine-tuning,
                and iterative testing cycles.&quot;
              </p>
            </div>
            <div className="rounded-lg border border-green-200 bg-green-50 p-5">
              <div className="flex items-center gap-2 text-green-700">
                <Icon icon="tabler:check" className="text-xl" />
                <span className="font-semibold">The Reality</span>
              </div>
              <p className="mt-2 text-green-800">
                Upload a document and have a fully functional AI chatbot answering questions in
                under 10 minutes. Really.
              </p>
            </div>
          </div>

          <h3>The Chat.co Timeline</h3>
          <div className="my-6 space-y-4">
            <div className="flex items-start gap-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-700">
                1
              </div>
              <div>
                <p className="font-semibold text-gray-900">Upload your content (1 minute)</p>
                <p className="text-sm text-gray-600">
                  Drag and drop PDFs, Word docs, or paste text directly
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-700">
                2
              </div>
              <div>
                <p className="font-semibold text-gray-900">Automatic processing (2-5 minutes)</p>
                <p className="text-sm text-gray-600">
                  Our system extracts, chunks, and indexes your content automatically
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-700">
                3
              </div>
              <div>
                <p className="font-semibold text-gray-900">Test and refine (2-3 minutes)</p>
                <p className="text-sm text-gray-600">
                  Preview your bot, adjust settings, and customize responses
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-green-100 text-sm font-semibold text-green-700">
                ✓
              </div>
              <div>
                <p className="font-semibold text-gray-900">Go live (1 minute)</p>
                <p className="text-sm text-gray-600">
                  Copy your embed code and deploy to your website
                </p>
              </div>
            </div>
          </div>

          <div className="my-6 rounded-lg border-l-4 border-green-500 bg-green-50 p-4">
            <p className="text-sm text-green-900">
              <strong>Pro Tip:</strong> Start with your FAQ document or knowledge base. You can
              always add more content later—your chatbot will get smarter with each upload.
            </p>
          </div>
        </section>

        {/* Myth 4: You Need Massive Data */}
        <section id="myth-data" className="scroll-mt-24">
          <h2>5. Myth: You Need Massive Datasets</h2>

          <div className="my-6 grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-red-200 bg-red-50 p-5">
              <div className="flex items-center gap-2 text-red-700">
                <Icon icon="tabler:x" className="text-xl" />
                <span className="font-semibold">The Myth</span>
              </div>
              <p className="mt-2 text-red-800">
                &quot;AI only works well with thousands of documents and millions of data points.
                Small businesses don&apos;t have enough data.&quot;
              </p>
            </div>
            <div className="rounded-lg border border-green-200 bg-green-50 p-5">
              <div className="flex items-center gap-2 text-green-700">
                <Icon icon="tabler:check" className="text-xl" />
                <span className="font-semibold">The Reality</span>
              </div>
              <p className="mt-2 text-green-800">
                Even a single well-written document can power an effective AI chatbot. Quality
                matters more than quantity.
              </p>
            </div>
          </div>

          <h3>Start Small, Grow Smart</h3>
          <div className="my-6 grid gap-4 sm:grid-cols-3">
            <div className="rounded-lg border border-gray-200 bg-white p-4 text-center">
              <Icon icon="tabler:file-text" className="mx-auto text-3xl text-blue-500" />
              <span className="mt-2 block text-2xl font-bold text-gray-900">1</span>
              <span className="text-sm text-gray-600">FAQ document</span>
              <p className="mt-1 text-xs text-gray-500">Perfect for getting started</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4 text-center">
              <Icon icon="tabler:files" className="mx-auto text-3xl text-purple-500" />
              <span className="mt-2 block text-2xl font-bold text-gray-900">5-10</span>
              <span className="text-sm text-gray-600">Product guides</span>
              <p className="mt-1 text-xs text-gray-500">Comprehensive support bot</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4 text-center">
              <Icon icon="tabler:database" className="mx-auto text-3xl text-green-500" />
              <span className="mt-2 block text-2xl font-bold text-gray-900">50+</span>
              <span className="text-sm text-gray-600">Documents</span>
              <p className="mt-1 text-xs text-gray-500">Enterprise knowledge base</p>
            </div>
          </div>

          <p>
            Chat.co uses advanced retrieval-augmented generation (RAG) technology that makes every
            piece of content count. Your bot retrieves exactly what it needs to answer each question
            accurately.
          </p>
        </section>

        {/* Myth 5: AI Is Only for Large Enterprises */}
        <section id="myth-enterprise" className="scroll-mt-24">
          <h2>6. Myth: AI Is Only for Large Enterprises</h2>

          <div className="my-6 grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-red-200 bg-red-50 p-5">
              <div className="flex items-center gap-2 text-red-700">
                <Icon icon="tabler:x" className="text-xl" />
                <span className="font-semibold">The Myth</span>
              </div>
              <p className="mt-2 text-red-800">
                &quot;Custom AI solutions are built for Fortune 500 companies with dedicated AI
                teams and unlimited budgets.&quot;
              </p>
            </div>
            <div className="rounded-lg border border-green-200 bg-green-50 p-5">
              <div className="flex items-center gap-2 text-green-700">
                <Icon icon="tabler:check" className="text-xl" />
                <span className="font-semibold">The Reality</span>
              </div>
              <p className="mt-2 text-green-800">
                Businesses of all sizes—from solo entrepreneurs to global corporations—use Chat.co
                to automate customer support.
              </p>
            </div>
          </div>

          <h3>Who Uses Chat.co?</h3>
          <div className="my-6 space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-orange-100">
                  <Icon icon="tabler:building-store" className="text-xl text-orange-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Small Businesses</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    Local shops and service providers using AI to answer customer questions 24/7
                    without hiring additional staff.
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100">
                  <Icon icon="tabler:rocket" className="text-xl text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Startups</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    Growing companies providing enterprise-quality support while keeping teams lean.
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-purple-100">
                  <Icon icon="tabler:building" className="text-xl text-purple-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Enterprises</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    Large organizations deploying multiple specialized bots across departments and
                    regions.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="my-6 rounded-lg border-l-4 border-blue-500 bg-blue-50 p-4">
            <p className="text-sm text-blue-900">
              <strong>The playing field is level:</strong> A 5-person startup can now offer the same
              AI-powered support experience as a company with hundreds of support agents.
            </p>
          </div>
        </section>

        {/* Myth 6: AI Will Replace Human Support */}
        <section id="myth-replace" className="scroll-mt-24">
          <h2>7. Myth: AI Will Replace Human Support Teams</h2>

          <div className="my-6 grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-red-200 bg-red-50 p-5">
              <div className="flex items-center gap-2 text-red-700">
                <Icon icon="tabler:x" className="text-xl" />
                <span className="font-semibold">The Myth</span>
              </div>
              <p className="mt-2 text-red-800">
                &quot;Implementing AI chatbots means firing your support team and leaving customers
                to talk to robots.&quot;
              </p>
            </div>
            <div className="rounded-lg border border-green-200 bg-green-50 p-5">
              <div className="flex items-center gap-2 text-green-700">
                <Icon icon="tabler:check" className="text-xl" />
                <span className="font-semibold">The Reality</span>
              </div>
              <p className="mt-2 text-green-800">
                AI augments your team by handling routine queries, freeing humans to focus on
                complex issues that need a personal touch.
              </p>
            </div>
          </div>

          <h3>The Human + AI Partnership</h3>
          <div className="my-6 overflow-hidden rounded-lg border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-900">AI Handles</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-900">Humans Handle</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr>
                  <td className="px-4 py-3 text-gray-700">Frequently asked questions</td>
                  <td className="px-4 py-3 text-gray-700">Complex problem-solving</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-gray-700">After-hours inquiries</td>
                  <td className="px-4 py-3 text-gray-700">Emotional support situations</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-gray-700">Product information lookup</td>
                  <td className="px-4 py-3 text-gray-700">Escalated complaints</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-gray-700">Basic troubleshooting</td>
                  <td className="px-4 py-3 text-gray-700">Relationship building</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p>
            The result? Your team handles fewer repetitive questions and more meaningful
            interactions. Customer satisfaction goes up, and so does job satisfaction.
          </p>
        </section>

        {/* Myth 7: AI Can't Be Accurate */}
        <section id="myth-accuracy" className="scroll-mt-24">
          <h2>8. Myth: AI Chatbots Can&apos;t Be Trusted for Accuracy</h2>

          <div className="my-6 grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-red-200 bg-red-50 p-5">
              <div className="flex items-center gap-2 text-red-700">
                <Icon icon="tabler:x" className="text-xl" />
                <span className="font-semibold">The Myth</span>
              </div>
              <p className="mt-2 text-red-800">
                &quot;AI chatbots make things up, hallucinate facts, and give unreliable answers
                that could embarrass your business.&quot;
              </p>
            </div>
            <div className="rounded-lg border border-green-200 bg-green-50 p-5">
              <div className="flex items-center gap-2 text-green-700">
                <Icon icon="tabler:check" className="text-xl" />
                <span className="font-semibold">The Reality</span>
              </div>
              <p className="mt-2 text-green-800">
                Chat.co uses RAG technology that grounds every response in your actual documents,
                complete with citations users can verify.
              </p>
            </div>
          </div>

          <h3>How Chat.co Ensures Accuracy</h3>
          <ul>
            <li>
              <strong>Source-grounded responses</strong> — Answers come directly from your uploaded
              content, not general internet knowledge
            </li>
            <li>
              <strong>Visible citations</strong> — Every response shows exactly which document the
              information came from
            </li>
            <li>
              <strong>Hallucination prevention</strong> — When the bot doesn&apos;t have
              information, it says so instead of making things up
            </li>
            <li>
              <strong>Easy content updates</strong> — Keep your knowledge base current by uploading
              new documents anytime
            </li>
          </ul>

          <div className="my-6 rounded-lg border-l-4 border-amber-500 bg-amber-50 p-4">
            <p className="text-sm text-amber-900">
              <strong>Best practice:</strong> Review your chatbot&apos;s responses periodically and
              keep your source documents up to date. The better your content, the better your
              bot&apos;s answers.
            </p>
          </div>
        </section>

        {/* Call to Action */}
        <section id="get-started" className="scroll-mt-24">
          <h2>9. Start Building Today</h2>
          <p>
            Now that you know the truth about AI chatbots, there&apos;s nothing holding you back.
            Building a sophisticated AI assistant is simpler, faster, and more affordable than you
            ever imagined.
          </p>

          <div className="my-8 rounded-xl border border-primary-200 bg-primary-50 p-6">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary-100">
                <Icon icon="tabler:rocket" className="text-xl text-primary-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary-900">
                  Ready to bust these myths yourself?
                </h4>
                <p className="mt-1 text-primary-800">
                  Create your first AI chatbot in minutes—no coding, no massive budget, no months of
                  waiting. See for yourself how easy it can be.
                </p>
              </div>
            </div>
          </div>

          <div className="my-6 grid gap-4 sm:grid-cols-2">
            <Link
              href={PageLink.docsGettingStarted}
              className="group rounded-lg border border-gray-200 bg-white p-6 transition-all hover:border-primary-200 hover:shadow-md">
              <Icon
                icon="tabler:rocket"
                className="text-2xl text-gray-400 group-hover:text-primary-500"
              />
              <h4 className="mt-3 font-semibold text-gray-900">Your First AI Agent</h4>
              <p className="mt-1 text-sm text-gray-600">
                Follow our step-by-step guide to build and deploy your chatbot in 10 minutes.
              </p>
            </Link>
            <Link
              href={PageLink.docsKnowledgeUpload}
              className="group rounded-lg border border-gray-200 bg-white p-6 transition-all hover:border-primary-200 hover:shadow-md">
              <Icon
                icon="tabler:upload"
                className="text-2xl text-gray-400 group-hover:text-primary-500"
              />
              <h4 className="mt-3 font-semibold text-gray-900">Knowledge Upload Playbook</h4>
              <p className="mt-1 text-sm text-gray-600">
                Learn best practices for organizing your content for maximum AI effectiveness.
              </p>
            </Link>
            <Link
              href={PageLink.pricing}
              className="group rounded-lg border border-gray-200 bg-white p-6 transition-all hover:border-primary-200 hover:shadow-md">
              <Icon
                icon="tabler:currency-dollar"
                className="text-2xl text-gray-400 group-hover:text-primary-500"
              />
              <h4 className="mt-3 font-semibold text-gray-900">View Pricing</h4>
              <p className="mt-1 text-sm text-gray-600">
                Explore plans for businesses of all sizes—from startups to enterprises.
              </p>
            </Link>
            <Link
              href={PageLink.login}
              className="group rounded-lg border border-gray-200 bg-white p-6 transition-all hover:border-primary-200 hover:shadow-md">
              <Icon
                icon="tabler:sparkles"
                className="text-2xl text-gray-400 group-hover:text-primary-500"
              />
              <h4 className="mt-3 font-semibold text-gray-900">Start 14-Day Trial</h4>
              <p className="mt-1 text-sm text-gray-600">Sign up and start using Chat.co today.</p>
            </Link>
          </div>

          <div className="my-8 rounded-xl border border-gray-200 bg-gray-50 p-6">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                <Icon icon="tabler:help" className="text-xl text-gray-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Still have questions?</h4>
                <p className="mt-1 text-gray-700">
                  Our team is here to help you succeed. Reach out at{' '}
                  <a
                    href="mailto:support@chat.co"
                    className="font-medium text-primary-600 hover:underline">
                    support@chat.co
                  </a>{' '}
                  or visit our{' '}
                  <Link
                    href={PageLink.contactUs}
                    className="font-medium text-primary-600 hover:underline">
                    Contact page
                  </Link>
                  .
                </p>
              </div>
            </div>
          </div>
        </section>
      </DocsArticleLayout>
    </>
  );
};

export const getServerSideProps = async () => {
  return { props: {} };
};

export default AiMythsPage;
